import { useState, ChangeEvent, useRef } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { useGetParsedDataQuery } from "store/apis/documents";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useToast } from "hooks/useToast";
import Loader from "components/DesignSystem/Loader/loader";

interface FormValues {
  prompt: string;
  files: File[];
}

const validationSchema = Yup.object().shape({
  prompt: Yup.string().required("Please enter a prompt"),
  files: Yup.array().min(1, "Please upload at least one file"),
});

const ParserAI = () => {
  const { alertToast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [prompt, setPrompt] = useState<string>("");
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const { data, error, isFetching, refetch } = useGetParsedDataQuery(
    {
      files: selectedFiles,
      prompt: prompt,
    },
    {
      skip: !shouldFetch,
      refetchOnMountOrArgChange: false,
    }
  );

  const handleFileSelect = (files: FileType[]) => {
    const fileArray = files as File[];
    setSelectedFiles(fileArray);
    if (formikRef.current) {
      formikRef.current.setFieldValue("files", fileArray);
    }
  };

  const handlePromptChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const handleDeleteFile = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    e.stopPropagation();
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    if (formikRef.current) {
      formikRef.current.setFieldValue("files", newFiles);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (values.files.length === 0) {
      alertToast({ message: "Please upload at least one file" });
      return;
    }

    setPrompt(values.prompt);
    setShouldFetch(true);
    await refetch();
    setShouldFetch(false);
  };

  return (
    <div className="t-container t-mx-auto t-py-8 t-px-4">
      <h1 className="t-text-subtitle-lg t-font-bold t-mb-6">
        Document Parser AI
      </h1>
      <div className="t-bg-white t-rounded-lg t-shadow-sm t-p-6">
        <Formik
          initialValues={{ prompt: "", files: [] as File[] }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          innerRef={formikRef}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="t-m-0 t-space-y-6">
              <div>
                <TextArea
                  name="prompt"
                  label="What would you like to know about these documents?"
                  required
                  placeholder="Enter your question or prompt here..."
                  rows={4}
                  block
                  onChange={handlePromptChange}
                />
              </div>

              <div>
                <FileInput
                  label="Upload Documents"
                  required
                  multiple
                  withForm
                  name="files"
                  // @ts-ignore
                  files={selectedFiles.map((file) => ({
                    name: file.name,
                    is_previewable: false,
                    file_type: file.type as string,
                    uuid: "",
                  }))}
                  onDelete={handleDeleteFile}
                  onDrop={handleFileSelect}
                />
                {/* {errors.files && touched.files && (
                  <div className="t-text-red t-mt-1 t-text-sm">
                    {errors.files}
                  </div>
                )} */}
              </div>

              <div className="t-flex t-justify-end">
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isSubmitting || isFetching}
                  disabled={isSubmitting || isFetching}
                >
                  Parse Documents
                </Button>
              </div>
            </Form>
          )}
        </Formik>

        {/* Results Section */}
        {isFetching ? (
          <div className="t-mt-8 t-flex t-justify-center">
            <p className="t-ml-3">Analyzing documents...</p>
          </div>
        ) : data ? (
          <div className="t-mt-8">
            <h2 className="t-text-lg t-font-semibold t-mb-4">Results</h2>
            <div className="t-bg-neutral-0 t-rounded-lg t-p-4 t-border t-border-neutral-10">
              <pre className="t-whitespace-pre-wrap t-text-text-100">
                {data.response}
              </pre>
            </div>
          </div>
        ) : error ? (
          <div className="t-mt-8 t-p-4 t-text-red t-rounded-lg">
            <h2 className="t-text-lg t-font-semibold t-mb-2">Error</h2>
            <p>
              {(error as any)?.data?.error?.message ||
                "There was an error processing your request. Please try again."}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ParserAI;
